import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, MapPin, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { VideoPlayer } from "../components/VideoPlayer";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { AwardGallery } from "../components/AwardGallery";
import { DOCTOR, IMAGES } from "../data/content";
import { useLang } from "../i18n/i18n";

function Portrait() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 60]);
  return (
    <div ref={ref} className="relative">
      <div className="absolute -inset-3 border border-crimson/25" />
      <div className="clip-corner relative h-[520px] w-full overflow-hidden bg-ink2 md:h-[640px]">
        <motion.img style={{ y }} src={IMAGES.aboutPortrait} alt={DOCTOR.name} loading="lazy" decoding="async" className="h-[115%] w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
      </div>
    </div>
  );
}

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="about-page">
      <PageHero
        index="/ 1"
        kicker={t.aboutPage.kicker}
        titleLines={[t.aboutPage.t1, t.aboutPage.t2]}
        subtitle={t.aboutPage.subtitle}
      />

      <section className="bg-ink px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <FadeUp>
            <Portrait />
          </FadeUp>

          <div className="space-y-6">
            <FadeUp>
              <div className="flex flex-wrap gap-3">
                <span className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/70">
                  <GraduationCap className="h-4 w-4 text-crimson" />
                  {t.about.role}
                </span>
                <span className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/70">
                  <MapPin className="h-4 w-4 text-crimson" />
                  {t.about.city}
                </span>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="text-xl leading-relaxed text-white/70">{t.aboutPage.lead}</p>
            </FadeUp>
            <FadeUp delay={0.18}>
              <p className="leading-relaxed text-white/55">{t.aboutPage.lead2}</p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="manifesto" className="relative overflow-hidden bg-ink2 px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-[1300px] space-y-24 md:space-y-40">
          {t.chapters.map((c, i) => (
            <FadeUp key={c.num}>
              <div className={`relative grid gap-8 md:grid-cols-2 md:items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                <div className="relative [direction:ltr]">
                  <span
                    className="pointer-events-none absolute -top-24 -left-4 select-none font-serif text-[12rem] leading-none text-stroke md:-top-32 md:text-[16rem]"
                    aria-hidden="true"
                  >
                    {c.num}
                  </span>
                </div>
                <div className="relative z-10 [direction:ltr]">
                  <div className="mb-5 flex items-center gap-4">
                    <span className="font-mono text-xs text-crimson">{c.num}</span>
                    <span className="h-px w-12 bg-crimson/50" />
                  </div>
                  <h3 className="font-serif text-4xl font-light text-white md:text-5xl">{c.title}</h3>
                  <p className="mt-6 leading-relaxed text-white/60">{c.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <EditorialMarquee dark />

      <AwardGallery />

      <section data-testid="about-video" className="bg-ink px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-[1300px]">
          <FadeUp>
            <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{t.aboutPage.videoKicker}</span>
                <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white md:text-5xl">{t.aboutPage.videoTitle}</h2>
              </div>
              <Link to="/media" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-white/70 transition-colors hover:text-crimson">
                {t.aboutPage.mediaLink}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <VideoPlayer />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
